import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const defaultForm = {
  title: '',
  type: 'bug',
  description: '',
  priority: 'medium',
  dueDate: '',
  status: 'open',
  requester: '',
};

const BugIssueForm = ({ isOpen, onClose, onSave, editingIssue, saving, requesterNickname, allUsers }) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingIssue) {
      setForm({
        title: editingIssue?.title || '',
        type: editingIssue?.type || 'bug',
        description: editingIssue?.description || '',
        priority: editingIssue?.priority || 'medium',
        dueDate: editingIssue?.dueDate || '',
        status: editingIssue?.status || 'open',
        requester: editingIssue?.requester || '',
      });
    } else {
      setForm({ ...defaultForm, requester: requesterNickname || '' });
    }
    setErrors({});
  }, [editingIssue, isOpen, requesterNickname]);

  const validate = () => {
    const errs = {};
    if (!form?.title?.trim()) errs.title = 'Title is required';
    return errs;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    onSave(form);
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Build dropdown options from allUsers, ensuring current requester is always present
  const userOptions = React.useMemo(() => {
    const users = allUsers || [];
    const options = users?.map(u => ({
      value: u?.nickname || u?.full_name || u?.email || '',
      label: u?.nickname
        ? `${u?.nickname}${u?.full_name ? ` (${u?.full_name})` : ''}`
        : (u?.full_name || u?.email || ''),
    }))?.filter(o => o?.value);

    // If current requester value isn't in the list, add it
    if (form?.requester && !options?.find(o => o?.value === form?.requester)) {
      options?.unshift({ value: form?.requester, label: form?.requester });
    }
    return options;
  }, [allUsers, form?.requester]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg mx-4 border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {editingIssue ? 'Edit Issue' : 'New Issue'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-muted text-muted-foreground">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Requester */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Requester</label>
            {userOptions?.length > 0 ? (
              <select
                value={form?.requester}
                onChange={(e) => set('requester', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">— Select requester —</option>
                {userOptions?.map(opt => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={form?.requester}
                onChange={(e) => set('requester', e?.target?.value)}
                placeholder="Requester nickname"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form?.title}
              onChange={(e) => set('title', e?.target?.value)}
              placeholder="Brief description of the issue"
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                errors?.title ? 'border-red-400' : 'border-border'
              }`}
            />
            {errors?.title && <p className="text-xs text-red-500 mt-1">{errors?.title}</p>}
          </div>

          {/* Type + Priority row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Type</label>
              <select
                value={form?.type}
                onChange={(e) => set('type', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="bug">Bug</option>
                <option value="enhancement">Enhancement</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
              <select
                value={form?.priority}
                onChange={(e) => set('priority', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={form?.description}
              onChange={(e) => set('description', e?.target?.value)}
              placeholder="Detailed description, steps to reproduce, expected behavior..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>

          {/* Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Due Date</label>
              <input
                type="date"
                value={form?.dueDate}
                onChange={(e) => set('dueDate', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          {/* Status (only when editing) */}
          {editingIssue && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <select
                value={form?.status}
                onChange={(e) => set('status', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (editingIssue ? 'Save Changes' : 'Create Issue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugIssueForm;
